import axios, { AxiosResponse, AxiosError } from 'axios'
import toast from 'react-hot-toast'
import type { ApiResponse, ApiError } from '@/types'
import { useAuthStore } from '@/stores/authStore'

// Feature flag: use httpOnly cookies for JWT (default true for production-first security)
const USE_COOKIE_TOKENS = (import.meta.env.VITE_USE_COOKIE_TOKENS || 'true').toLowerCase() === 'true'

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: USE_COOKIE_TOKENS,
})

// Case conversion utilities
const isObject = (val: any) => val && typeof val === 'object' && !Array.isArray(val)
const toSnakeKey = (s: string) => s
  .replace(/([A-Z])/g, '_$1')
  .replace(/[-\s]+/g, '_')
  .toLowerCase()
  .replace(/__+/g, '_')
  .replace(/^_/, '')

const toSnake = (input: any): any => {
  if (Array.isArray(input)) return input.map(toSnake)
  if (!isObject(input)) return input
  const out: any = {}
  for (const [k, v] of Object.entries(input || {})) {
    const nk = toSnakeKey(k)
    out[nk] = toSnake(v)
  }
  return out
}
const toCamelKey = (s: string) => s.replace(/[_-][a-z]/g, (m) => m[1].toUpperCase())
  .replace(/\s+([a-z])/g, (_: any, c: string) => c.toUpperCase())

const toCamel = (input: any): any => {
  if (Array.isArray(input)) return input.map(toCamel)
  if (input && typeof input === 'object') {
    const out: any = {}
    for (const [k, v] of Object.entries(input)) {
      const cv = toCamel(v)
      const ck = toCamelKey(k)
      out[ck] = cv
    }
    return out
  }
  return input
}


// Token management
const TOKEN_KEY = 'bonnycloud_token'

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY)
}

export const setToken = (token: string): void => {
  if (USE_COOKIE_TOKENS) return // no-op when using cookies
  localStorage.setItem(TOKEN_KEY, token)
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

export const removeToken = (): void => {
  if (!USE_COOKIE_TOKENS) {
    localStorage.removeItem(TOKEN_KEY)
    delete api.defaults.headers.common['Authorization']
  }
}

// Set token on app start if it exists
if (!USE_COOKIE_TOKENS) {
  const existingToken = getToken()
  if (existingToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`
  }
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    if (config.method === 'get') {
      config.params = { ...config.params, _t: Date.now() }
    }
    // Normalize params/body casing
    if ((config as any).params) {
      (config as any).params = toSnake((config as any).params)
    }
    // Add CSRF header when using cookie tokens for state-changing requests
    if (USE_COOKIE_TOKENS && config.method && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
      const csrf = getCookie('csrf_access_token')
      if (csrf) {
        (config.headers as any)['X-CSRF-TOKEN'] = csrf
      }
    }

    // Add X-Tenant-ID header for tenant scoping
    // Get current tenant ID from localStorage (tenant store persists it there)
    const tenantStorageKey = 'bonnycloud-current-tenant-id'
    const storedTenantId = localStorage.getItem(tenantStorageKey)
    if (storedTenantId) {
      (config.headers as any)['X-Tenant-ID'] = storedTenantId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'))
  return match ? decodeURIComponent(match[2]) : null
}

// Response interceptor
let lastSessionToastAt = 0

api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    return response
  },
  (error: AxiosError<ApiResponse>) => {
    const apiError: ApiError = {
      message: 'An error occurred',
      status: error.response?.status || 500,
      details: error.response?.data,
    }

    // Handle specific error cases
    if (error.response?.status === 401) {
      // For auth endpoints, do not redirect; show message instead
      const url = (error.config?.url || '').toString()
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register')
      const isSessionCheck = url.includes('/auth/me')  // Silent session validation
      const backendMsg = (error.response.data as any)?.error || (error.response.data as any)?.message

      if (isAuthEndpoint) {
        apiError.message = backendMsg || 'Invalid credentials'
      } else if (isSessionCheck) {
        // Silent fail for session check - no toast, no logout call
        return Promise.reject(apiError)
      } else {
        // End session in client state; ProtectedRoute will handle navigation
        try {
          useAuthStore.getState().logout()
        } catch {}
        removeToken()
        apiError.message = backendMsg || 'Session expired. Please login again.'
        // Rate-limit the toast to avoid loops
        const now = Date.now()
        if (now - lastSessionToastAt < 10000) {
          // Skip showing toast repeatedly
          return Promise.reject(apiError)
        }
        lastSessionToastAt = now
      }
    } else if (error.response?.status === 403) {
      apiError.message = 'Access denied. You do not have permission to perform this action.'
    } else if (error.response?.status === 404) {
      apiError.message = 'Resource not found.'
    } else if (error.response?.status === 422) {
      apiError.message = 'Validation failed. Please check your input.'
    } else if (error.response?.status === 429) {
      apiError.message = 'Too many requests. Please try again later.'
    } else if (error.response && error.response.status >= 500) {
      apiError.message = 'Server error. Please try again later.'
    } else if (error.code === 'ECONNABORTED') {
      apiError.message = 'Request timeout. Please check your connection.'
    } else if (error.code === 'ERR_NETWORK') {
      apiError.message = 'Network error. Please check your internet connection.'
    } else {
      const respData = (error.response?.data ?? {}) as any
      if (respData?.error) {
        apiError.message = respData.error
      } else if (respData?.message) {
        apiError.message = respData.message
      }
    }

    // Show error toast for non-auth errors
    if (!(error.response?.status === 401 && (error.config?.url || '').toString().includes('/auth/login'))) {
      toast.error(apiError.message)
    }

    return Promise.reject(apiError)
  }
)

// Generic API methods
export const apiClient = {
  get: <T = any>(url: string, params?: any): Promise<AxiosResponse<ApiResponse<T>>> =>
    api.get(url, { params }),

  getRaw: (url: string, config?: any) =>
    api.get(url, config),
  
  post: <T = any>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> =>
    api.post(url, toSnake(data)),

  postForm: (url: string, formData: FormData, config?: any) =>
    api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' }, ...config }),
  
  put: <T = any>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> =>
    api.put(url, toSnake(data)),
  
  delete: <T = any>(url: string): Promise<AxiosResponse<ApiResponse<T>>> =>
    api.delete(url),
  
  patch: <T = any>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> =>
    api.patch(url, toSnake(data)),
}

// Utility function to handle API responses
export const handleApiResponse = <T>(response: AxiosResponse<ApiResponse<T>>): T => {
  const anyData: any = response.data as any
  if (anyData && anyData.error) {
    throw new Error(anyData.error)
  }
  const payload: any = anyData?.data ?? anyData
  return toCamel(payload) as T
}

// Health check endpoint
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    await api.get('/health')
    return true
  } catch (error) {
    return false
  }
}

// OTA Updates API service
export const otaUpdates = {
  // Get OTA versions (admin only)
  getVersions: (params?: {
    releaseType?: string
    isActive?: boolean
  }) => api.get('/ota/versions', { params }),

  // Create new OTA version (admin only)
  createVersion: (data: {
    version: string
    releaseType?: string
    description: string
    changelog?: string
    filePath: string
    isCritical?: boolean
    supportedModels?: string[]
    minDeviceVersion?: string
    maxDeviceVersion?: string
    requiresReboot?: boolean
    estimatedInstallTime?: number
    minFreeSpace?: number
    rollbackVersion?: string
  }) => api.post('/ota/versions', data),

  // Update OTA version (admin only)
  updateVersion: (versionId: number, data: any) => api.put(`/ota/versions/${versionId}`, data),

  // Get update logs (admin only)
  getUpdateLogs: (params?: {
    deviceId?: string
    versionId?: number
    status?: string
    limit?: number
    offset?: number
  }) => api.get('/ota/update-logs', { params }),

  // Get update policies (admin only)
  getPolicies: () => api.get('/ota/policies'),

  // Create update policy (admin only)
  createPolicy: (data: {
    name: string
    description?: string
    autoUpdate?: boolean
    autoUpdateCritical?: boolean
    updateChannel?: string
    updateWindowStart?: string
    updateWindowEnd?: string
    updateDays?: number[]
    maxRetryAttempts?: number
    retryDelay?: number
    autoRollbackOnFailure?: boolean
    stagedRollout?: boolean
    stagedRolloutPercentage?: number
    priority?: number
  }) => api.post('/ota/policies', data),

  // Get public key for signature verification
  getPublicKey: () => api.get('/ota/public-key'),

  // Device endpoints (for Raspberry Pi)
  checkForUpdates: (data: {
    device_id: string
    current_version: string
    device_model: string
  }) => api.post('/ota/check', data),

  // Download update package
  downloadUpdate: (version: string, deviceId: string, deviceToken: string) =>
    api.get(`/ota/download/${version}`, {
      headers: {
        'X-Device-ID': deviceId,
        'X-Device-Token': deviceToken
      }
    }),

  // Report update status
  reportStatus: (data: {
    version: string
    status: string
    progress?: number
    error_message?: string
    download_speed?: number
    system_metrics?: any
    signature_verified?: boolean
    hash_verified?: boolean
    rollback_reason?: string
  }, deviceId: string, deviceToken: string) =>
    api.post('/ota/report', data, {
      headers: {
        'X-Device-ID': deviceId,
        'X-Device-Token': deviceToken
      }
    })
};

// KI-Bon API service
export const kibon = {
  // Generate AI content
  generate: (data: {
    prompt: string
    outputFormat?: string
    provider?: string
    model?: string
    temperature?: number
    maxTokens?: number
    templateName?: string
    category?: string
    printerId?: string  // For printer width detection
  }) => api.post('/ki-bon/generate', data),

  // Generate and print in one step
  generateAndPrint: (data: {
    prompt: string
    printerId: string
    outputFormat?: string
    provider?: string
    model?: string
    temperature?: number
    maxTokens?: number
    printSettings?: any
  }) => api.post('/ki-bon/generate-and-print', data),

  // Get user's KI-Bon history
  getHistory: (params?: {
    limit?: number
    offset?: number
    status?: string
    category?: string
    outputFormat?: string
  }) => api.get('/ki-bon/history', { params }),

  // Get specific KI-Bon request
  getHistoryItem: (requestId: number) => api.get(`/ki-bon/history/${requestId}`),

  // Rate a KI-Bon request
  rateRequest: (requestId: number, data: {
    rating?: number
    feedback?: string
    is_favorite?: boolean
  }) => api.post(`/ki-bon/history/${requestId}/rate`, data),

  // Get prompt templates
  getTemplates: () => api.get('/ki-bon/templates'),

  // Admin: Manage templates
  getAllTemplates: () => api.get('/admin/ai/templates'),
  createTemplate: (data: any) => api.post('/admin/ai/templates', data),
  updateTemplate: (data: any) => api.post('/admin/ai/templates', data),
  deleteTemplate: (templateId: number) => api.delete(`/admin/ai/templates/${templateId}`),

  // Validate a prompt
  validatePrompt: (prompt: string) => api.post('/ki-bon/validate-prompt', { prompt }),

  // Get available AI providers
  getProviders: () => api.get('/ki-bon/providers'),

  // Get usage statistics
  getStats: () => api.get('/ki-bon/stats'),

  // Get favorite requests
  getFavorites: () => api.get('/ki-bon/favorites'),

  // Repeat a previous request
  repeatRequest: (requestId: number, modifications?: any) =>
    api.post(`/ki-bon/repeat/${requestId}`, modifications || {}),

  // Print existing content without regenerating
  printContent: (data: {
    content: string
    printerId: string
    metadata?: any
    printSettings?: any
    priority?: number
  }) => api.post('/ki-bon/print', data),

  // Get available output formats (user view - active only)
  getOutputFormats: () => api.get('/ki-bon/output-formats'),

  // Admin: Manage output formats
  getAllOutputFormats: () => api.get('/admin/ai/output-formats'),
  createOutputFormat: (data: any) => api.post('/admin/ai/output-formats', data),
  updateOutputFormat: (formatId: number, data: any) => api.put(`/admin/ai/output-formats/${formatId}`, data),
  deleteOutputFormat: (formatId: number) => api.delete(`/admin/ai/output-formats/${formatId}`),
}

// Tenant API service
export const tenants = {
  // Get all tenants for current user
  getAll: () => api.get('/tenants'),

  // Get specific tenant
  get: (tenantId: number) => api.get(`/tenants/${tenantId}`),

  // Create new tenant
  create: (data: {
    name: string
    tenantType: 'organization' | 'location' | 'department'
    parentId?: number
    description?: string
    settings?: any
    resourceLimits?: any
  }) => api.post('/tenants', data),

  // Update tenant
  update: (tenantId: number, data: any) => api.put(`/tenants/${tenantId}`, data),

  // Delete tenant
  delete: (tenantId: number) => api.delete(`/tenants/${tenantId}`),

  // Get tenant members
  getMembers: (tenantId: number) => api.get(`/tenants/${tenantId}/members`),

  // Add member to tenant
  addMember: (tenantId: number, data: {
    userId: number
    tenantRole: 'admin' | 'member' | 'viewer'
    customPermissions?: any
  }) => api.post(`/tenants/${tenantId}/members`, data),

  // Update member role/permissions
  updateMember: (tenantId: number, data: {
    userId: number
    tenantRole?: string
    customPermissions?: any
  }) => api.put(`/tenants/${tenantId}/members`, data),

  // Remove member from tenant
  removeMember: (tenantId: number, userId: number) =>
    api.delete(`/tenants/${tenantId}/members?user_id=${userId}`),

  // Get tenant invites
  getInvites: (tenantId: number) => api.get(`/tenants/${tenantId}/invites`),

  // Create invite
  createInvite: (tenantId: number, data: {
    tenantRole: 'admin' | 'member' | 'viewer'
    maxUses?: number
    expiresIn?: number
  }) => api.post(`/tenants/${tenantId}/invites`, data),

  // Delete invite
  deleteInvite: (tenantId: number, inviteId: number) =>
    api.delete(`/tenants/${tenantId}/invites/${inviteId}`),

  // Join tenant with invite code
  joinWithInvite: (data: { inviteCode: string }) =>
    api.post('/tenants/join', data),

  // Get tenant printers
  getPrinters: (tenantId: number) => api.get(`/tenants/${tenantId}/printers`),

  // Get tenant devices
  getDevices: (tenantId: number) => api.get(`/tenants/${tenantId}/devices`),

  // Get tenant statistics
  getStats: (tenantId: number) => api.get(`/tenants/${tenantId}/stats`),

  // Get current user's memberships
  getMemberships: () => api.get('/tenants/memberships'),

  // Switch active tenant
  switchActive: (tenantId: number) => api.post(`/tenants/switch/${tenantId}`),
}

export default api
